/**
 * Invoice B2C - Main App
 * =======================
 * Standalone B2C frontend with local auth (no Portal SSO).
 * Pages are lazy-loaded for smaller initial bundle (auth pages excepted).
 */

import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './shared/i18n';
import { allTranslations } from './i18n';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { DateFormatProvider } from './context/DateFormatContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { CategoryProvider } from './context/CategoryContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import CookieBanner from './components/common/CookieBanner';
import MarketingRedirect from './components/MarketingRedirect';
import { isAuthShellRoute } from './pages/auth/authShellRoutes';
import posthog from './lib/posthog';
import { appAreaFromPath, captureAppEvent } from './lib/posthogTracking';

// Global styles
import './App.scss';

// Auth pages load eagerly — common entry points; lazy Suspense spinner flashes
// white over the green entrance animation.
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SsoCallbackPage from './pages/auth/SsoCallbackPage';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

/** Manual SPA $pageview (SDK has capture_pageview: false). Path only — ignore query scrub noise. */
function PostHogPageviews() {
    const location = useLocation();
    useEffect(() => {
        try {
            const locale = document.documentElement.lang || 'en';
            posthog.register({
                locale,
                app_area: appAreaFromPath(location.pathname),
            });
            captureAppEvent('$pageview');
        } catch { /* analytics must not break navigation */ }
    }, [location.pathname]);
    return null;
}

/** Keep html[data-auth-route] (+ white shell for ?vt=white) in sync with the route. */
function AuthShellBackground() {
    const { pathname, search } = useLocation();
    useLayoutEffect(() => {
        if (!isAuthShellRoute(pathname)) {
            document.documentElement.removeAttribute('data-auth-route');
            document.documentElement.removeAttribute('data-auth-vt');
            return;
        }
        document.documentElement.setAttribute('data-auth-route', '');
        const vt = new URLSearchParams(search).get('vt')?.toLowerCase();
        // Preserve parse-time white shell after ?vt= is stripped; only force green for vt=green
        if (vt === 'white') {
            document.documentElement.setAttribute('data-auth-vt', 'white');
        } else if (vt === 'green') {
            document.documentElement.removeAttribute('data-auth-vt');
        }
    }, [pathname, search]);
    return null;
}

// Lazy-loaded app pages — each becomes a separate chunk
const InvoiceList = React.lazy(() => import('./pages/InvoiceList'));
const RevenueList = React.lazy(() => import('./pages/RevenueList'));
const InvoiceDetail = React.lazy(() => import('./pages/InvoiceDetail'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Settings = React.lazy(() => import('./pages/Settings'));
const BankStatements = React.lazy(() => import('./pages/BankStatements'));
const BankStatementDetail = React.lazy(() => import('./pages/BankStatementDetail'));
const Trash = React.lazy(() => import('./pages/Trash'));
const Reconciliation = React.lazy(() => import('./pages/Reconciliation'));
const Files = React.lazy(() => import('./pages/Files'));

const PageLoader = () => (
    <div className="loading-container">
        <div className="spinner" />
    </div>
);

function App() {
    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <AuthShellBackground />
            <PostHogPageviews />
            <LanguageProvider translations={allTranslations}>
            <AuthProvider>
            <ThemeProvider>
            <DateFormatProvider>
            <CategoryProvider>
            <OnboardingProvider>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    {/* Public marketing routes — moved to Astro at https://kontomatch.com.
                        These redirects exist so old links and bookmarks land on the new site. */}
                    <Route path="/" element={<MarketingRedirect />} />
                    <Route path="/about" element={<MarketingRedirect />} />
                    <Route path="/contact" element={<MarketingRedirect />} />
                    <Route path="/how-it-works" element={<MarketingRedirect />} />
                    <Route path="/faq" element={<MarketingRedirect />} />
                    <Route path="/security" element={<MarketingRedirect />} />
                    <Route path="/pricing" element={<MarketingRedirect />} />
                    <Route path="/articles" element={<MarketingRedirect />} />
                    <Route path="/articles/:slug" element={<MarketingRedirect />} />
                    <Route path="/legal/:slug" element={<MarketingRedirect />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/verify" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/auth/sso-callback" element={<SsoCallbackPage />} />

                    {/* Protected routes */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute><Dashboard /></ProtectedRoute>
                    } />
                    <Route path="/invoices" element={
                        <ProtectedRoute><InvoiceList /></ProtectedRoute>
                    } />
                    <Route path="/invoices/:id" element={
                        <ProtectedRoute><InvoiceDetail /></ProtectedRoute>
                    } />
                    <Route path="/revenue" element={
                        <ProtectedRoute><RevenueList /></ProtectedRoute>
                    } />
                    <Route path="/revenue/:id" element={
                        <ProtectedRoute><InvoiceDetail /></ProtectedRoute>
                    } />
                    <Route path="/bank-statements" element={
                        <ProtectedRoute><BankStatements /></ProtectedRoute>
                    } />
                    <Route path="/bank-statements/transactions" element={
                        <ProtectedRoute><BankStatements /></ProtectedRoute>
                    } />
                    <Route path="/bank-statements/:id" element={
                        <ProtectedRoute><BankStatementDetail /></ProtectedRoute>
                    } />
                    <Route path="/reconciliation" element={
                        <ProtectedRoute><Reconciliation /></ProtectedRoute>
                    } />
                    <Route path="/trash" element={
                        <ProtectedRoute><Trash /></ProtectedRoute>
                    } />
                    <Route path="/files" element={
                        <ProtectedRoute><Files /></ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                        <ProtectedRoute><Settings /></ProtectedRoute>
                    } />

                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
                <OnboardingWizard />
                <TutorialOverlay />
                <CookieBanner />
            </OnboardingProvider>
            </CategoryProvider>
            </DateFormatProvider>
            </ThemeProvider>
            </AuthProvider>
            </LanguageProvider>
        </Router>
    );
}

export default App;
